class SpellService {
    constructor() {
        this.cache = new Map();
        this.pending = new Map();
        this.otherCache = null;
        this.otherPending = null;
    }

    getKey(typeId) {
        if (!typeId) return null;
        return String(typeId);
    }

    normalizeData(data) {
        return {
            spells: Array.isArray(data?.spells) ? data.spells : [],
            other: Array.isArray(data?.other) ? data.other : [],
            summonCatalog: data?.summonCatalog && typeof data.summonCatalog === 'object'
                ? data.summonCatalog
                : {},
        };
    }

    async getOtherSpells(force = false) {
        if (force) {
            this.otherCache = null;
            this.otherPending = null;
        }
        if (this.otherCache !== null) return this.otherCache;
        if (this.otherPending) return this.otherPending;

        this.otherPending = (async () => {
            const mod = await import(
                /* webpackChunkName: "spell-data-other" */
                '../data/spells/other.json'
            );
            const raw = mod?.default || mod;
            const data = Array.isArray(raw?.other) ? raw.other : [];
            this.otherCache = data;
            return data;
        })();

        try {
            return await this.otherPending;
        } finally {
            this.otherPending = null;
        }
    }

    getCachedClassSpells(typeId) {
        const key = this.getKey(typeId);
        return key ? this.cache.get(key) || null : null;
    }

    async getClassSpells(typeId, opts = {}) {
        const { force = false } = opts;
        const key = this.getKey(typeId);
        if (!key) return { spells: [], other: [], summonCatalog: {} };

        if (force) {
            this.cache.delete(key);
        }

        if (!force && this.cache.has(key)) {
            return this.cache.get(key);
        }

        if (!force && this.pending.has(key)) {
            return this.pending.get(key);
        }

        const req = (async () => {
            const [mod, other] = await Promise.all([
                import(
                    /* webpackChunkName: "spell-data-[request]" */
                    `../data/spells/${key}.json`
                ),
                this.getOtherSpells(force),
            ]);
            const classData = mod?.default || mod;
            const data = this.normalizeData({
                ...classData,
                other: Array.isArray(classData?.other) ? classData.other : other,
            });
            this.cache.set(key, data);
            return data;
        })();

        this.pending.set(key, req);
        try {
            return await req;
        } finally {
            this.pending.delete(key);
        }
    }

    prefetchClassSpells(typeId) {
        return this.getClassSpells(typeId).catch(() => ({ spells: [], other: [], summonCatalog: {} }));
    }

    clearCache(typeId = null) {
        const key = this.getKey(typeId);
        if (key) {
            this.cache.delete(key);
            this.pending.delete(key);
            return;
        }
        this.cache.clear();
        this.pending.clear();
        this.otherCache = null;
        this.otherPending = null;
    }
}

const service = new SpellService();
export default service;
